/**
 * Low-level, platform-safe blur helpers for static `expo-image` usage.
 *
 * **Summary:** Never applies `blurRadius` on Android (RenderScript / GROKIO-APP-9C). iOS and web
 * use `blurRadius`; Android 12+ uses `style.filter: blur(Npx)` via `RenderEffect`. Older Android
 * gets no blur from these helpers — callers rely on overlays.
 *
 * **Prefer** {@link BlurredExpoImage} in app code. Pass an {@link IExpoImageBlurConfig} or use
 * presets (e.g. `LOCKED_CONTENT_BLUR` in `@/constants/lockedContentBlur`).
 *
 * **Related (not in this file):**
 * - Full crash context, usage, clipping: `components/ui/BlurredExpoImage.tsx`
 * - Profile header scroll blur: `components/domain/profile/ProfileScreen.tsx` (inline `PROFILE_SCROLL_BLUR_CONFIG`)
 * - Image viewer backdrop (no Android blur): `components/ui/image-viewer/ImageViewerBackground.tsx`
 *
 * @module expoImageBlur
 */

import { Platform, type ViewStyle } from 'react-native';
/** Per-platform blur strengths for {@link BlurredExpoImage} and {@link getExpoImageBlurProps}. */
export interface IExpoImageBlurConfig {
  /** iOS `blurRadius` (SDWebImage / expo-image). */
  iosBlurRadius: number;
  /** Web `blurRadius` (often lower than iOS numerically for similar perceived strength). */
  webBlurRadius: number;
  /** Android 12+ CSS `filter: blur(Npx)` (not 1:1 with `blurRadius`). */
  androidCssBlurPx: number;
}

/**
 * Whether this device should use CSS `filter: blur()` on views (Android 12+ only).
 * Uses RN `RenderEffect`, not Glide/RenderScript (GROKIO-APP-9C).
 */
export function supportsAndroidCssBlur(): boolean {
  return Platform.OS === 'android' && Platform.Version >= 31;
}

function usesExpoImageBlurRadius(): boolean {
  return Platform.OS === 'ios' || Platform.OS === 'web';
}

/** CSS filter string for RN `style.filter`. JS thread only (not a Reanimated worklet). */
export function cssBlurFilter(blurPx: number): string {
  return `blur(${blurPx}px)`;
}

/** Clip CSS blur to rounded bounds. Used by `BlurredExpoImage` and `ProfileScreen`. */
export const ANDROID_CSS_BLUR_CLIP_STYLE: ViewStyle = { overflow: 'hidden' };

function getExpoImageBlurRadius(config: IExpoImageBlurConfig): number {
  if (!usesExpoImageBlurRadius()) {
    return 0;
  }
  if (Platform.OS === 'web') {
    return config.webBlurRadius;
  }
  return config.iosBlurRadius;
}

function getExpoImageBlurFilterStyle(config: IExpoImageBlurConfig): ViewStyle | undefined {
  if (!supportsAndroidCssBlur()) {
    return undefined;
  }
  return { filter: cssBlurFilter(config.androidCssBlurPx) } as ViewStyle;
}

/**
 * True when `BlurredExpoImage` should wrap the image in an `overflow: hidden`
 * container and overscan the inner image. Required on platforms that implement
 * blur via CSS `filter: blur()` (Android 12+ via `RenderEffect`, web via expo-image's
 * `blurRadius` -> CSS filter): the blur kernel samples pixels outside the source
 * bounds — those are transparent, so a fringe of half-transparent pixels appears
 * along each edge. iOS uses SDWebImage's bitmap-level blur which clamps at edges,
 * so no wrapper is needed.
 */
export function needsCssBlurOverscanClip(
  blur: IExpoImageBlurConfig | null | undefined
): boolean {
  return blur != null && (supportsAndroidCssBlur() || Platform.OS === 'web');
}

/**
 * Position style for the inner image inside the `overflow: hidden` wrapper from
 * {@link needsCssBlurOverscanClip}. The image is rendered slightly larger than its
 * parent so the CSS-blur transparent-edge fringe ends up outside the visible area
 * and is clipped away. Overscan is proportional to the blur strength: ~1.35x the
 * effective radius safely covers the Gaussian kernel's transparency falloff.
 */
export function getCssBlurOverscanStyle(
  blur: IExpoImageBlurConfig
): ViewStyle | undefined {
  if (!supportsAndroidCssBlur() && Platform.OS !== 'web') {
    return undefined;
  }
  const blurPx = Platform.OS === 'web' ? blur.webBlurRadius : blur.androidCssBlurPx;
  const overscan = Math.max(2, Math.ceil(blurPx * 1.35));
  return {
    position: 'absolute',
    top: -overscan,
    bottom: -overscan,
    left: -overscan,
    right: -overscan,
  };
}

/**
 * Platform-safe blur props for a static `expo-image`.
 * Pass `null`/`undefined` for no blur. Prefer {@link BlurredExpoImage}.
 */
export function getExpoImageBlurProps(
  blur: IExpoImageBlurConfig | null | undefined
): { blurRadius: number; style?: ViewStyle } {
  if (!blur) {
    return { blurRadius: 0 };
  }

  const filterStyle = getExpoImageBlurFilterStyle(blur);

  return {
    blurRadius: getExpoImageBlurRadius(blur),
    ...(filterStyle ? { style: filterStyle } : {}),
  };
}
